import { isPlatformBrowser, Location } from '@angular/common';
import { ChangeDetectorRef, computed, Directive, Inject, inject, PLATFORM_ID } from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Store } from '@ngrx/store';
import { AppConfigService } from 'sharedServices/app-config.service';
import { QuoteSelectors } from 'stores/quote/quote.module';
import { ScriptInjectionService } from 'sharedGlobals/gtm-data-layer/script-injection.service';
import { ActivatedRoute, Router } from '@angular/router';
import {
    QuoteControllerActions,
    QuoteControllerSelectors,
} from 'sharedStores/quote-controller/quote-controller.module';
import { WindowABTestVariantService } from 'services/window-ab-test-variant.service';
import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { FocusService } from 'sharedServices/focus.service';
import {
    selectLeadNumber,
    selectPolicyDetails,
    selectQuoteState,
    selectUserId,
} from 'stores/quote/quote.selectors';
import { filter, tap } from 'rxjs/operators';
import { combineLatest } from 'rxjs';
import { AppModeService } from 'sharedServices/app-mode.service';
import { QuoteControllerService_Token } from 'shared/base/generics/injection-tokens/dependency-tokens';
import { selectCmsState } from 'sharedStores/cms/cms.selectors';
import { selectHomeContents } from 'stores/home-contents/home-contents.selectors';
import { selectPaymentDetails } from 'stores/payment-details/payment.selectors';
import {
    selectPolicyHolder,
    selectHouseholdMembers,
    selectPersonalDetailsDocumentState,
    selectPersonalDetailsState,
} from 'stores/personal/personal.selectors';
import { selectStaticDataState } from 'stores/static-data/static-data.selectors';
import { selectVehicle } from 'stores/vehicle/vehicle.selectors';
import { BasePageComponent } from 'sharedGlobals/base-page-component/base-page.component';
import { DebugSectionNames } from 'sharedEnums/debug-section-names';
import { PendingRequestsService } from 'sharedServices/pending-requests.service';

@UntilDestroy()
@Directive()
export class QuoteBasePage extends BasePageComponent {
    readonly abTestVariants = this.store.selectSignal(QuoteControllerSelectors.getABTestVariants);
    readonly userId = this.store.select(selectUserId);
    readonly leadNumber = this.store.selectSignal(selectLeadNumber);
    readonly quoteState = this.store.selectSignal(selectQuoteState);
    readonly vehicleState = this.store.selectSignal(selectVehicle);
    readonly homeContentsState = this.store.selectSignal(selectHomeContents);
    readonly policyHolderState = this.store.selectSignal(selectPolicyHolder);
    readonly householdMemberState = this.store.selectSignal(selectHouseholdMembers);
    readonly cmsState = this.store.selectSignal(selectCmsState);
    readonly policyState = this.store.selectSignal(selectPolicyDetails);
    readonly staticDataState = this.store.selectSignal(selectStaticDataState);
    readonly personalDetailsDocumentState = this.store.selectSignal(
        selectPersonalDetailsDocumentState,
    );
    readonly personalDetailsState = this.store.selectSignal(selectPersonalDetailsState);
    readonly paymentDetailsState = this.store.selectSignal(selectPaymentDetails);

    readonly combinedPersonalDetails = computed(() => ({
        document: this.personalDetailsDocumentState(),
        details: this.personalDetailsState(),
    }));

    public quoteController = inject(QuoteControllerService_Token);

    constructor(
        public store: Store,
        public location: Location,
        public appConfig: AppConfigService,
        @Inject(PLATFORM_ID) public readonly platformId: Object,
        public gtmService: ScriptInjectionService,
        public cRef: ChangeDetectorRef,
        public activatedRoute: ActivatedRoute,
        public windowABTestVariantService: WindowABTestVariantService,
        public breakpointObserver: BreakpointObserver,
        public focusService: FocusService,
        public router: Router,
        public appModeService: AppModeService,
        public readonly pendingRequestsService: PendingRequestsService,
    ) {
        super(appConfig, gtmService, appModeService, pendingRequestsService);
        this.breakpointObserver
            .observe([Breakpoints.Handset])
            .pipe(untilDestroyed(this))
            .subscribe((result) => {
                this.isMobile = result.matches;
            });

        this.focusService.focus$.pipe(untilDestroyed(this)).subscribe((isFocused) => {
            this.isFocused = isFocused;
        });

        this.isBrowser = isPlatformBrowser(this.platformId);
        this.watchABTestVariantChanges();

        this.store
            .select(QuoteSelectors.selectPremiumViewed)
            .pipe(untilDestroyed(this))
            .subscribe((viewed) => {
                this.premiumViewed = viewed;
                this.cRef.markForCheck();
            });

        this.quoteController.activePage$.pipe(untilDestroyed(this)).subscribe((activePage) => {
            this.quoteController.updatePageConfig(activePage?.page);
            this.cRef.markForCheck();
        });

        this.assetsUrl = computed(
            () => `${this.appConfig.config().cmsUrl}/api/assets/con-fusion-static-data`,
        );

        this.envDebugDrawer = computed(() => this.appConfig.config().debugDrawer === 'true');

        // add userID to query params
        combineLatest([
            this.userId.pipe(filter((userId: string) => !!userId)),
            this.activatedRoute.queryParams,
        ])
            .pipe(
                untilDestroyed(this),
                tap(([userId, queryParams]) => {
                    if (queryParams['userId'] !== userId) {
                        this.router.navigate([], {
                            relativeTo: this.activatedRoute,
                            queryParams: { ...queryParams, userId },
                            queryParamsHandling: 'merge',
                            replaceUrl: true,
                        });
                    }
                }),
            )
            .subscribe();

        this.debugSections = [
            {
                label: DebugSectionNames.QUOTE_STATE,
                state: this.quoteState,
            },
            {
                label: DebugSectionNames.VEHICLE_STATE,
                state: this.vehicleState,
            },
            {
                label: DebugSectionNames.HOME_CONTENTS_STATE,
                state: this.homeContentsState,
            },
            {
                label: DebugSectionNames.PERSONAL_DETAILS_STATE,
                state: this.combinedPersonalDetails,
            },
            {
                label: DebugSectionNames.POLICY_HOLDER_STATE,
                state: this.policyHolderState,
            },
            {
                label: DebugSectionNames.HOUSEHOLD_MEMBERS_STATE,
                state: this.householdMemberState,
            },
            {
                label: DebugSectionNames.PAYMENT_DETAILS_STATE,
                state: this.paymentDetailsState,
            },
            {
                label: DebugSectionNames.POLICY_STATE,
                state: this.policyState,
            },
            {
                label: DebugSectionNames.STATIC_DATA_STATE,
                state: this.staticDataState,
            },
            {
                label: DebugSectionNames.CMS_STATE,
                state: this.cmsState,
            },
        ];
    }

    private watchABTestVariantChanges() {
        this.windowABTestVariantService.variantHasChanged$().subscribe((hasChanged) => {
            if (hasChanged) {
                this.store.dispatch(
                    QuoteControllerActions.setABTestVariantsValues({
                        abTestVariants: window.ab_test_variants,
                    }),
                );
            }
        });
    }

    onDebugClick() {
        if (this.envDebugDrawer() === true) {
            this.showDebug = !this.showDebug;
        }
    }

    onBackClick() {
        this.quoteController.back();
    }
}
